

import React, { useState } from 'react'
import { FormCheckbox, FormTextarea } from 'protocol-setup/components/FormComponents';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { postWithAuth } from 'protocol-setup/redux/makeFetch';
import { defaultErrorMessage, regulatorySignOffSubmitted, requestValidationPath } from 'protocol-setup/redux/constants';
import { OpenErrorNotification, OpenSuccessNotification } from 'protocol-setup/components/notification';
import { dispatchRefreshEvent, useSetupPermission } from "protocol-setup/helpers";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import ButtonGroupSignoff from 'protocol-setup/components/FormComponents/ButtonGroupSignoff';
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import text from "protocol-setup/constants/text";
import errorMessages, { COMMENTS_MAX_LENGTH } from "protocol-setup/constants/errorMessages";
import { regTabNames } from "../regHelper";

export const schema = yup.object().shape({
  groupAdminSignoff: yup.boolean().oneOf([true], errorMessages.regulatory.signOff.acknowledgmentRequired),
  groupAdminComments: yup.string().max(COMMENTS_MAX_LENGTH, errorMessages.regulatory.signOff.submitForm.groupAdminComments.max).optional(),
  irocAdminSignoffRequired: yup.mixed().oneOf(["", true, false])
});

export const handleSubmitForm = async (data, requestNumber, onSuccessCallback) => {
  const { groupAdminSignoff, groupAdminComments, irocAdminSignoffRequired } = data;
  try {
    const payload = {
      requestNumber,
      groupAdminSignoff: Boolean(groupAdminSignoff),
      groupAdminComments,
      irocAdminSignoffRequired: Boolean(irocAdminSignoffRequired)
    }
    await postWithAuth(regulatorySignOffSubmitted, payload);
    if (onSuccessCallback) {
      await onSuccessCallback();
    }
    OpenSuccessNotification({
      message: "Record successfully saved.",
      autoHide: true
    });
    return true;
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
    return false;
  }
};


const RegulatorySubmitForm = ({ protocolSetupData, fetchPsData, readOnly }) => {
  const { requestNumber, regSignoff } = protocolSetupData;
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isSubmitting, dirtyFields }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      groupAdminSignoff: regSignoff?.groupAdminSignoff || '',
      groupAdminComments: regSignoff?.groupAdminComments || '',
      irocAdminSignoffRequired: regSignoff?.irocAdminSignoffRequired || ''
    }
  });
  const [showWarning, setShowWarning] = useState(false);
  const hasSignOffPermission = useSetupPermission(ProtectionElements.REGULATORY_SIGNOFF_SUBMITTED);
  const isSignOffEditable = !readOnly && hasSignOffPermission;

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(data, requestNumber, onSuccessCallback),
    dirtyFields,
    regTabNames.SIGNOFF_SUBMITTED,
    "Signoff"
  );

  const onReset = () => {
    reset();
  };

  const validateSetup = async (data) => {
    const { lpo, protocolNumber } = data;
    if (lpo && protocolNumber) {
      const requestBody = {
        lpo,
        protocol: protocolNumber,
        formPartType: 'OPEN_RAVE'
      };
      try {
        const response = await postWithAuth(requestValidationPath, requestBody);
        if (response && Array.isArray(response)) {
          if (response.length === 0) {
            setShowWarning(true)
          } else if (response.length > 0) {
            const warningStatuses = [
              'DRAFT',
              'SUBMITTED',
              'UAT_SETUP',
              'GROUP_TESTING',
              'CTSU_TESTING'
            ]
            const hasWarningStatus = Array.isArray(response) &&
              response.some(item => warningStatuses.includes(item?.requestStatus))
            setShowWarning(hasWarningStatus)
          } else {
            setShowWarning(false)
          }
        }
      } catch (error) {
        // catch error
      }
    }
  };

  useState(() => {
    if (!readOnly) {
      validateSetup(protocolSetupData)
    }
  }, [protocolSetupData, readOnly])

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${readOnly ? "read-only" : ""
        }`}
    >      <form
      id="RegSubmitForm"
      className="bg-transparent mt-3"
      onSubmit={handleSubmit((data) => {
        const d = { ...data, requestNumber }
        onSubmitWithHistory(d, onSuccess)
      }
      )}
    >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul>
            <li className="row mb-0">
              <div className="col-8 mr-4 mb-0">
                <FormCheckbox
                  className="pl-0 d-flex align-items-start"
                  labelClassName="color-dark-blue align-left col-12"
                  name="groupAdminSignoff"
                  section="regulatory"
                  form="regulatorySubmitForm"
                  required
                  control={control}
                  disabled={!isSignOffEditable}
                  readOnly={!isSignOffEditable}
                  wrapperClassName={`mb-3 ${!isSignOffEditable ? 'read-only-show' : ''}`}
                  trigger={trigger}
                />
                {showWarning ? <span className='pl-0 mb-3 form-layout-message-warning mt-1'>
                  {text.regulatory.signOff.submitForm.warning}
                </span> : null}
              </div>
            </li>
            <li className="row mb-0">
              <div className="col-12 mr-4 mb-0">
                <FormTextarea
                  name="groupAdminComments"
                  section="regulatory"
                  form="regulatorySubmitForm"
                  control={control}
                  disabled={!isSignOffEditable}
                  trigger={trigger}
                  wrapperClassName="mb-3"
                />
              </div>
            </li>
            <li className="row mb-0">
              <div className="col-12 mr-4 mb-0">
                <FormCheckbox
                  className="pl-0 mb-3"
                  labelClassName="color-dark-blue align-left col-12"
                  name="irocAdminSignoffRequired"
                  section="regulatory"
                  form="regulatorySubmitForm"
                  control={control}
                  disabled={!isSignOffEditable}
                  readOnly={!isSignOffEditable}
                  wrapperClassName={!isSignOffEditable ? 'read-only-show' : ''}
                  trigger={trigger}
                />
              </div>
            </li>
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          {isSignOffEditable && (
            <ButtonGroupSignoff
              onSubmit={handleSubmitForm}
              onReset={onReset}
              isSubmitting={isSubmitting}
              control={control}
              protectionElement={ProtectionElements.REGULATORY_SIGNOFF_SUBMITTED}
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default RegulatorySubmitForm;